import { useContext, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { useUser, useClerk } from '@clerk/clerk-react';
import { PlaycastContext } from '../../components/PlaycastWrapper';
import {
  PAVILION_SHELL_BORDER,
  PAVILION_SHELL_GRADIENT,
  PAVILION_SHELL_RADIUS,
} from './pavilionShellStyles';

interface AccountInfoPageProps {
  onBack: () => void;
}

const BackArrowIcon = () => (
  <svg width="39" height="34" viewBox="0 0 39 34" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M0.816158 18.9527C-0.272072 17.8492 -0.272072 16.057 0.816158 14.9534L14.7455 0.827908C15.5464 0.0156898 16.7391 -0.222679 17.7838 0.218742C18.8285 0.660168 19.4989 1.6931 19.4989 2.83197V11.3073L34.8212 11.3073C37.1282 11.3073 39 13.2054 39 15.5449V18.37C39 20.7096 37.1282 22.6077 34.8212 22.6077H19.4989V31.083C19.4989 32.2219 18.8198 33.2548 17.7751 33.6962C16.7304 34.1377 15.5377 33.8905 14.7368 33.0871L0.807453 18.9615L0.816158 18.9527Z"
      fill="currentColor"
    />
  </svg>
);

export const AccountInfoPage = ({ onBack }: AccountInfoPageProps) => {
  const context = useContext(PlaycastContext);
  const { user } = useUser();
  const { signOut } = useClerk();
  const [isWindowBlurred, setIsWindowBlurred] = useState(false);
  const [confirmSignOut, setConfirmSignOut] = useState(false);

  useEffect(() => {
    let mounted = true;
    window.electron?.on('pavilion-window-blur', () => {
      if (mounted) setIsWindowBlurred(true);
    });
    window.electron?.on('pavilion-window-focus', () => {
      if (mounted) setIsWindowBlurred(false);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const handleClose = () => {
    context.electronFunctions?.setWindowState('closed');
  };

  const handleSignOut = async () => {
    if (!confirmSignOut) {
      setConfirmSignOut(true);
      return;
    }
    await signOut();
    window.location.reload();
  };

  const primaryEmail = user?.primaryEmailAddress?.emailAddress;
  const primaryPhone = user?.primaryPhoneNumber?.phoneNumber;
  const username = user?.username;
  const showPhoneFallback = !primaryEmail && primaryPhone;

  return (
    <div className={`box-border flex h-full min-h-0 w-full flex-1 overflow-hidden ${isWindowBlurred ? PAVILION_SHELL_GRADIENT : 'bg-transparent'}`}>
      <div
        className={`relative box-border flex h-full min-h-0 w-full flex-col overflow-hidden ${PAVILION_SHELL_RADIUS} ${PAVILION_SHELL_BORDER} ${PAVILION_SHELL_GRADIENT} pt-5 pb-5 pl-[7px] pr-[7px]`}
      >
        <button
          type='button'
          onClick={handleClose}
          className='absolute right-2 top-2 z-10 flex size-9 items-center justify-center rounded-md text-[#FBBF24] transition-colors hover:text-[#FCD34D] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FBBF24]'
          aria-label='Close'
        >
          <FontAwesomeIcon icon={faXmark} className='size-5' />
        </button>

        <div className='flex min-h-0 flex-1 flex-col items-center justify-center gap-4'>
          <h1 className='shrink-0 text-center text-[36px] tracking-tight font-["Inter"] text-[#FEF3C7]'>
            Account Info
          </h1>

          {username && (
            <div className='flex flex-col items-center gap-1'>
              <h2 className='shrink-0 text-center text-[32px] font-["Inter"] text-[#FFFFFF]'>
                Username
              </h2>
              <p className='shrink-0 text-center text-[24px] font-["Inter"] text-[#FFFFFF]'>
                {username}
              </p>
            </div>
          )}

          {showPhoneFallback ? (
            <div className='flex flex-col items-center gap-1'>
              <h2 className='shrink-0 text-center text-[32px] font-["Inter"] text-[#FFFFFF]'>
                Phone Number
              </h2>
              <p className='shrink-0 text-center text-[24px] font-["Inter"] text-[#FFFFFF]'>
                {primaryPhone}
              </p>
            </div>
          ) : primaryEmail ? (
            <div className='flex flex-col items-center gap-1'>
              <h2 className='shrink-0 text-center text-[32px] font-["Inter"] text-[#FFFFFF]'>
                Email
              </h2>
              <p className='shrink-0 text-center text-[24px] font-["Inter"] text-[#FFFFFF] break-all px-2'>
                {primaryEmail}
              </p>
            </div>
          ) : null}
        </div>

        <div className='flex shrink-0 flex-col items-center gap-4 pb-2'>
          <button
            type='button'
            onClick={handleSignOut}
            className={`rounded-full px-10 py-2.5 text-center text-lg font-semibold transition-all ${
              confirmSignOut
                ? 'bg-[#991B1B] text-[#FCA5A5]'
                : 'bg-[#FCA5A5] text-[#991B1B]'
            }`}
          >
            {confirmSignOut ? 'You Sure?' : 'Sign Out'}
          </button>

          <button
            type='button'
            onClick={onBack}
            className='self-start pl-4 text-[#FEF3C7] transition-opacity hover:opacity-80'
            aria-label='Back'
          >
            <BackArrowIcon />
          </button>
        </div>
      </div>
    </div>
  );
};
